import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  SphereGeometry,
  Vector3,
} from 'three';
import React, { useEffect, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';

import { Billboard } from '@react-three/drei';
import { PHYSICS } from '../constants';

// Simple particle system for hit effects
const HitParticles = ({ visible }: { visible: boolean }) => {
  const particlesRef = useRef<Group>(null);
  const particles = useRef<Mesh[]>([]);
  const velocities = useRef<Vector3[]>([]);

  // Create particles on mount
  useEffect(() => {
    if (!particlesRef.current) return;

    // Create 10 particles
    for (let i = 0; i < 10; i++) {
      const particle = new Mesh(
        new SphereGeometry(0.05, 8, 8),
        new MeshBasicMaterial({ color: '#FF0000' })
      );

      // Position particles at center first
      particle.position.set(0, 0, 0);
      particle.visible = false;

      particlesRef.current.add(particle);
      particles.current.push(particle);

      // Create random velocity
      velocities.current.push(
        new Vector3(
          (Math.random() - 0.5) * 2,
          Math.random() * 2,
          (Math.random() - 0.5) * 2
        )
      );
    }
  }, []);

  // Reset and show particles when becoming visible
  useEffect(() => {
    if (!visible || !particlesRef.current) return;

    // Reset all particles to center and make visible
    particles.current.forEach((particle, i) => {
      particle.position.set(0, 0.5, 0); // Start slightly above center
      particle.visible = true;

      // Refresh velocities
      velocities.current[i] = new Vector3(
        (Math.random() - 0.5) * 2,
        Math.random() * 2,
        (Math.random() - 0.5) * 2
      );
    });

    // Hide particles after animation completes
    const timeout = setTimeout(() => {
      particles.current.forEach((particle) => {
        particle.visible = false;
      });
    }, 500);

    return () => clearTimeout(timeout);
  }, [visible]);

  // Animate particles
  useFrame((_, delta) => {
    if (!visible || !particlesRef.current) return;

    particles.current.forEach((particle, i) => {
      if (!particle.visible) return;

      // Move particle
      particle.position.add(
        velocities.current[i].clone().multiplyScalar(delta * 5)
      );

      // Apply gravity
      velocities.current[i].y -= delta * 5;

      // Fade out by scaling down
      particle.scale.multiplyScalar(0.95);
    });
  });

  return <group ref={particlesRef} />;
};

// Define the network projectile interface (for type safety)
interface NetworkProjectile {
  id: string;
  position: [number, number, number];
  playerId: string;
  characterType?: string; // Add character type
}

interface SimpleSasukeProps {
  position: [number, number, number];
  updateDebugInfo?: (data: any) => void;
  isControlled?: boolean;
  isCurrentPlayer?: boolean;
  facingDirection?: [number, number, number];
  isJumping?: boolean;
  playerId?: string;
  networkShurikens?: NetworkProjectile[];
  networkRasengans?: NetworkProjectile[];
  health?: number;
  isDead?: boolean;
}

export const SimpleSasuke: React.FC<SimpleSasukeProps> = ({
  position: initialPosition,
  updateDebugInfo,
  isControlled = true,
  facingDirection: initialFacingDirection,
  isJumping: initialIsJumping = false,
  networkShurikens,
  networkRasengans, // We'll use this for network fireballs
  health = 100,
  isDead = false,
}) => {
  // console.log({ position: initialPosition, isCurrentPlayer });
  const GROUND_OFFSET = PHYSICS.GROUND_OFFSET;
  const GROUND_LEVEL = PHYSICS.GROUND_LEVEL;
  const sasukeRef = useRef<Group>(null);
  const { scene } = useThree(); // Access the Three.js scene

  // Remove local tracking of projectiles, we'll only use network projectiles now
  // The network handling will create/manage all projectiles

  // Add hit effect state
  const [isHit, setIsHit] = useState(false);
  const prevHealth = useRef(health);
  const knockbackVector = useRef(new Vector3());

  // Detect when player is hit by comparing current and previous health
  useEffect(() => {
    // If health decreased, player was hit
    if (health < prevHealth.current) {
      // Show hit effect
      setIsHit(true);

      // Apply knockback - a small random direction
      if (!isControlled) {
        // Only apply visual knockback for non-controlled players
        knockbackVector.current.set(
          (Math.random() - 0.5) * 0.5,
          0.2, // Small upward boost
          (Math.random() - 0.5) * 0.5
        );
      }

      // Hide hit effect after a short delay
      setTimeout(() => setIsHit(false), 300);
    }

    // Update previous health
    prevHealth.current = health;
  }, [health, isControlled]);

  // Core state managed via refs for performance
  const state = useRef({
    position: new Vector3(...initialPosition).setY(
      Math.max(initialPosition[1], GROUND_LEVEL) - GROUND_OFFSET
    ),
    velocity: new Vector3(),
    facingDirection: initialFacingDirection
      ? new Vector3(...initialFacingDirection)
      : new Vector3(0, 0, -1),
    isJumping: initialIsJumping,
    armRotation: 0,
    legRotation: 0,
    isRunning: false,
    lastFireballTime: 0, // Track the last time a Fireball was thrown
    lastShurikenTime: 0, // Track the last time a Shuriken was thrown
  });

  // Update keys to include fireball and shuriken input
  const keys = useRef({
    forward: false,
    backward: false,
    left: false,
    right: false,
    jump: false,
    run: false,
    rasengan: false, // Reuse rasengan key for fireball for network compatibility
    shuriken: false, // Add shuriken key
  });

  const groundLevel = initialPosition[1] - GROUND_OFFSET;
  const lastUpdateTime = useRef(0);
  const updateInterval = 1 / 30;

  // Input handling
  useEffect(() => {
    if (!isControlled) return;

    // Restore direct keyboard event listeners for desktop
    const handleKeyDown = (e: KeyboardEvent) => {
      e.preventDefault();
      switch (e.key.toLowerCase()) {
        case 'w':
        case 'arrowup':
          keys.current.forward = true;
          break;
        case 's':
        case 'arrowdown':
          keys.current.backward = true;
          break;
        case 'a':
        case 'arrowleft':
          keys.current.left = true;
          break;
        case 'd':
        case 'arrowright':
          keys.current.right = true;
          break;
        case ' ': // Space bar for jump
          if (!state.current.isJumping) {
            keys.current.jump = true;
            state.current.isJumping = true;
            state.current.velocity.y = PHYSICS.JUMP_FORCE; // Use the shared jump force constant
          }
          break;
        case 'shift':
          keys.current.run = true;
          break;
        case 'r': // Add 'R' key for Fireball
          keys.current.rasengan = true;
          break;
        case 'f': // Add 'F' key for Shuriken
          keys.current.shuriken = true;
          break;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      switch (e.key.toLowerCase()) {
        case 'w':
        case 'arrowup':
          keys.current.forward = false;
          break;
        case 's':
        case 'arrowdown':
          keys.current.backward = false;
          break;
        case 'a':
        case 'arrowleft':
          keys.current.left = false;
          break;
        case 'd':
        case 'arrowright':
          keys.current.right = false;
          break;
        case ' ':
          keys.current.jump = false;
          break;
        case 'shift':
          keys.current.run = false;
          break;
        case 'r': // Reset fireball key
          keys.current.rasengan = false;
          break;
        case 'f': // Reset shuriken key
          keys.current.shuriken = false;
          break;
      }
    };

    const handleBlur = () => {
      keys.current = {
        forward: false,
        backward: false,
        left: false,
        right: false,
        jump: false,
        run: false,
        rasengan: false,
        shuriken: false,
      };
    };

    // Also listen for control updates from mobile controls
    const handleControlsUpdate = (e: any) => {
      if (e.detail && e.detail.keys) {
        // Merge with existing keys to ensure desktop and mobile controls work together
        keys.current = { ...keys.current, ...e.detail.keys };
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('controlsupdate', handleControlsUpdate);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('controlsupdate', handleControlsUpdate);
    };
  }, [isControlled]);

  // Effect to handle position updates from server for non-controlled players
  useEffect(() => {
    if (isControlled) return; // Skip for locally controlled player

    // Update position with smooth interpolation
    const targetPosition = new Vector3(
      initialPosition[0],
      Math.max(initialPosition[1], GROUND_LEVEL) - GROUND_OFFSET, // Ensure Y doesn't go below ground
      initialPosition[2]
    );

    // Don't snap immediately, let the animation loop handle interpolation
    state.current.position = targetPosition.clone();

    // Update facing direction if provided
    if (initialFacingDirection) {
      state.current.facingDirection = new Vector3(...initialFacingDirection);
      // Immediately apply rotation based on facingDirection
      if (sasukeRef.current && state.current.facingDirection.length() > 0) {
        sasukeRef.current.rotation.y = Math.atan2(
          state.current.facingDirection.x,
          state.current.facingDirection.z
        );
      }
    }

    // Update jumping state if provided
    if (initialIsJumping !== undefined) {
      state.current.isJumping = initialIsJumping;
    }
  }, [initialPosition, initialFacingDirection, initialIsJumping, isControlled]);

  // Effect to handle local player position updates (including respawns)
  useEffect(() => {
    if (!isControlled || !sasukeRef.current) return;

    // Check if position changed significantly (like a respawn)
    const currentPosition = state.current.position;
    const newPosition = new Vector3(...initialPosition).setY(
      Math.max(initialPosition[1], GROUND_LEVEL) - GROUND_OFFSET
    );

    // If the change is large (>10 units), it's likely a respawn - snap to it
    if (currentPosition.distanceTo(newPosition) > 10) {
      state.current.position.copy(newPosition);
      sasukeRef.current.position.copy(newPosition);
    }
  }, [initialPosition, isControlled]);

  // Handle ALL projectiles via network (for both local and remote players)
  const networkProjectilesRef = useRef({
    shurikens: new Map<string, Group>(),
    fireballs: new Map<string, Mesh>(), // Renamed from rasengans to fireballs
  });

  // Handle network shurikens - for ALL players including local
  useEffect(() => {
    if (!networkShurikens) return;

    const currentIds = new Set<string>();

    // Create or update ALL shurikens from network
    networkShurikens.forEach((shuriken) => {
      currentIds.add(shuriken.id);

      // If we don't have this shuriken yet, create it
      if (!networkProjectilesRef.current.shurikens.has(shuriken.id)) {
        const position = new Vector3(...shuriken.position);
        const group = createShurikenGroup(position);
        scene.add(group);
        networkProjectilesRef.current.shurikens.set(shuriken.id, group);
      } else {
        // Update existing shuriken position
        const group = networkProjectilesRef.current.shurikens.get(shuriken.id)!;
        group.position.set(...shuriken.position);
        group.rotation.z += 0.1; // Add some rotation for animation
      }
    });

    // Remove shurikens that are no longer in the network state
    networkProjectilesRef.current.shurikens.forEach((group, id) => {
      if (!currentIds.has(id)) {
        scene.remove(group);
        networkProjectilesRef.current.shurikens.delete(id);
      }
    });
  }, [networkShurikens, scene]);

  // Handle network fireballs (using network rasengans) - for ALL players including local
  useEffect(() => {
    if (!networkRasengans) return;

    const currentIds = new Set<string>();

    // Create or update ALL fireballs from network
    networkRasengans.forEach((rasengan) => {
      currentIds.add(rasengan.id);

      // If we don't have this fireball yet, create it
      if (!networkProjectilesRef.current.fireballs.has(rasengan.id)) {
        const position = new Vector3(...rasengan.position);

        // Create appropriate mesh based on character type
        const mesh = new Mesh(
          new SphereGeometry(0.3, 16, 16),
          new MeshStandardMaterial(
            rasengan.characterType === 'sasuke'
              ? {
                  // Sasuke's fireball (orange/red)
                  color: '#FF4500', // Red-orange color
                  emissive: '#FF7F50', // Coral emissive color for glow
                  emissiveIntensity: 2,
                  toneMapped: false,
                }
              : {
                  // Naruto's rasengan (blue)
                  color: '#00BFFF', // Bright blue color
                  emissive: '#0080FF', // Glowing effect
                  emissiveIntensity: 2,
                  toneMapped: false,
                }
          )
        );
        mesh.position.copy(position);
        scene.add(mesh);
        networkProjectilesRef.current.fireballs.set(rasengan.id, mesh);
      } else {
        // Update existing fireball position
        const mesh = networkProjectilesRef.current.fireballs.get(rasengan.id)!;
        mesh.position.set(...rasengan.position);
        mesh.rotation.x += 0.1; // Add some rotation for animation
        mesh.rotation.y += 0.1;
      }
    });

    // Remove fireballs that are no longer in the network state
    networkProjectilesRef.current.fireballs.forEach((mesh, id) => {
      if (!currentIds.has(id)) {
        scene.remove(mesh);
        networkProjectilesRef.current.fireballs.delete(id);
      }
    });
  }, [networkRasengans, scene]);

  // Create shuriken mesh function
  const createShurikenGroup = (position: Vector3): Group => {
    const group = new Group();
    group.position.copy(position);

    // Center part
    const centerMesh = new Mesh(
      new CylinderGeometry(0.05, 0.05, 0.03, 16),
      new MeshStandardMaterial({
        color: '#555555',
        metalness: 0.8,
        roughness: 0.2,
      })
    );
    group.add(centerMesh);

    // Four blades
    for (let i = 0; i < 4; i++) {
      const blade = new Mesh(
        new BoxGeometry(0.2, 0.05, 0.01),
        new MeshStandardMaterial({
          color: '#303030',
          metalness: 0.9,
          roughness: 0.1,
        })
      );

      // Position each blade at 90 degree intervals
      blade.position.set(
        0.12 * Math.cos((i * Math.PI) / 2),
        0.12 * Math.sin((i * Math.PI) / 2),
        0
      );
      blade.rotation.set(0, 0, (i * Math.PI) / 2);
      group.add(blade);
    }

    return group;
  };

  // Main game loop
  useFrame((_, delta) => {
    if (!sasukeRef.current) return;

    const s = state.current;
    const cappedDelta = Math.min(delta, 0.1);
    const speed = 10 * cappedDelta * (keys.current.run ? 2 : 1);
    const jumpPower = 10;

    // Movement
    const moveVector = new Vector3();
    if (isControlled && !isDead) {
      if (keys.current.forward) moveVector.z -= 1;
      if (keys.current.backward) moveVector.z += 1;
      if (keys.current.left) moveVector.x -= 1;
      if (keys.current.right) moveVector.x += 1;
    }

    const isMoving = moveVector.length() > 0;
    if (isMoving) {
      moveVector.normalize().multiplyScalar(speed);
      s.position.add(moveVector);
      s.facingDirection.copy(moveVector).normalize();
      sasukeRef.current.rotation.y = Math.atan2(moveVector.x, moveVector.z);

      const animSpeed = keys.current.run ? 15 : 10;
      s.armRotation =
        Math.sin(_.clock.elapsedTime * animSpeed) *
        (keys.current.run ? 0.8 : 0.5);
      s.legRotation = Math.sin(_.clock.elapsedTime * animSpeed) * 0.6;
      s.isRunning = keys.current.run;
    } else {
      s.armRotation *= 0.9;
      s.legRotation *= 0.9;
      s.isRunning = false;
    }

    // Jumping - check keys.current.jump state for both keyboard and mobile
    if (
      isControlled &&
      !isDead &&
      keys.current.jump &&
      !s.isJumping &&
      s.position.y <= groundLevel + 0.01
    ) {
      s.velocity.y = jumpPower;
      s.isJumping = true;
    }

    // Physics
    s.velocity.y -= PHYSICS.GRAVITY * delta;
    s.position.y += s.velocity.y * cappedDelta;

    // Apply gravity if we're in the air
    if (s.position.y > GROUND_LEVEL - GROUND_OFFSET && !keys.current.jump) {
      s.velocity.y -= PHYSICS.GRAVITY * delta; // Apply gravity
    }

    // Ensure we don't fall below ground level
    if (s.position.y < GROUND_LEVEL - GROUND_OFFSET) {
      s.position.y = GROUND_LEVEL - GROUND_OFFSET;
      s.velocity.y = 0;
      s.isJumping = false;
    }

    // Fireball logic - ONLY send to server, don't create locally
    if (
      isControlled &&
      !isDead &&
      keys.current.rasengan &&
      _.clock.elapsedTime - s.lastFireballTime > 2
    ) {
      // Calculate position and direction
      const position = s.position
        .clone()
        .add(s.facingDirection.clone().multiplyScalar(0.5))
        .setY(s.position.y + 0.8);

      // Notify server about this fireball
      // Note: We're reusing the throwRasengan event for network compatibility
      if (updateDebugInfo) {
        updateDebugInfo({
          throwRasengan: {
            position: position.toArray(),
            direction: s.facingDirection.toArray(),
            characterType: 'sasuke', // Specify character type
          },
        });
      }

      // Update cooldown
      s.lastFireballTime = _.clock.elapsedTime;
    }

    // Shuriken logic - ONLY send to server, don't create locally
    if (
      isControlled &&
      !isDead &&
      keys.current.shuriken &&
      _.clock.elapsedTime - s.lastShurikenTime > 0.4
    ) {
      // Calculate position and direction
      const position = s.position
        .clone()
        .add(s.facingDirection.clone().multiplyScalar(0.5))
        .setY(s.position.y + 0.9);

      // Notify server about this shuriken
      if (updateDebugInfo) {
        updateDebugInfo({
          throwShuriken: {
            position: position.toArray(),
            direction: s.facingDirection.toArray(),
            characterType: 'sasuke', // Specify character type
          },
        });
      }

      // Update cooldown
      s.lastShurikenTime = _.clock.elapsedTime;
    }

    // Update mesh
    if (isControlled) {
      // For controlled player, directly update position
      sasukeRef.current.position.copy(s.position);
    } else {
      // For non-controlled players, use interpolation for smooth movement
      const targetPosition = new Vector3(
        initialPosition[0],
        Math.max(initialPosition[1], GROUND_LEVEL) - GROUND_OFFSET, // Enforce ground level
        initialPosition[2]
      );

      // Apply knockback if hit
      if (isHit && knockbackVector.current.length() > 0) {
        // Add knockback to position
        targetPosition.add(knockbackVector.current);

        // Reduce knockback for next frame
        knockbackVector.current.multiplyScalar(0.8);
      }

      s.position.lerp(targetPosition, 0.2);
      sasukeRef.current.position.copy(s.position);

      // Apply rotation based on facingDirection for non-controlled players
      if (s.facingDirection.length() > 0) {
        sasukeRef.current.rotation.y = Math.atan2(
          s.facingDirection.x,
          s.facingDirection.z
        );
      }
    }

    // Throttled debug/server sync
    const now = _.clock.elapsedTime;
    if (
      isControlled &&
      updateDebugInfo &&
      now - lastUpdateTime.current > updateInterval
    ) {
      updateDebugInfo({
        position: s.position.toArray(),
        facingDirection: s.facingDirection.toArray(),
        isJumping: s.isJumping,
        // Remove local projectile counts since we don't track them locally anymore
      });
      lastUpdateTime.current = now;
    }
  });

  const { armRotation, legRotation, isRunning } = state.current;

  // Determine character color based on hit state
  const bodyColor = isHit ? '#FF0000' : '#0D47A1'; // Red when hit, dark blue otherwise
  const skinColor = isHit ? '#FFCCCC' : '#FFE0B2'; // Lighter red when hit, skin color otherwise
  const hairColor = '#191919'; // Dark black for Sasuke's hair

  return (
    <group ref={sasukeRef} position={state.current.position}>
      {/* Health bar - now using Billboard to always face camera */}
      {health !== undefined && (
        <Billboard
          position={[0, 2.2, 0]}
          follow={true}
          lockX={false}
          lockY={false}
          lockZ={false}
        >
          {/* Background of health bar */}
          <mesh position={[0, 0, 0]} scale={[1, 0.15, 0.05]}>
            <boxGeometry />
            <meshBasicMaterial color="#333333" />
          </mesh>

          {/* Fill of health bar - scales with health */}
          <mesh
            position={[(health / 100 - 1) / 2, 0, 0.03]}
            scale={[health / 100, 0.12, 0.05]}
          >
            <boxGeometry />
            <meshBasicMaterial
              color={
                health > 50 ? '#00FF00' : health > 20 ? '#FFFF00' : '#FF0000'
              }
            />
          </mesh>
        </Billboard>
      )}

      {/* Hit particles effect */}
      <HitParticles visible={isHit} />

      {/* Simplified Sasuke model - modified from Naruto model with different colors */}
      <mesh castShadow position={[0, 0.45, 0]}>
        <boxGeometry args={[0.85, 1.2, 0.45]} /> {/* Slightly slimmer body */}
        <meshLambertMaterial color={bodyColor} flatShading />
      </mesh>
      <mesh position={[0, 1.2, 0]} castShadow>
        <boxGeometry args={[0.75, 0.8, 0.8]} /> {/* Slightly smaller head */}
        <meshLambertMaterial color={skinColor} flatShading />
      </mesh>

      {/* Sasuke's spiky hairstyle - main hair */}
      <mesh position={[0, 1.5, 0]} castShadow>
        <boxGeometry args={[0.9, 0.4, 0.8]} />
        <meshLambertMaterial color={hairColor} flatShading />
      </mesh>

      {/* Sasuke's spiky hair back part */}
      <mesh position={[0, 1.5, -0.3]} castShadow>
        <boxGeometry args={[0.8, 0.3, 0.3]} />
        <meshLambertMaterial color={hairColor} flatShading />
      </mesh>

      {/* Forehead protector */}
      <mesh position={[0, 1.3, 0.4]} castShadow>
        <boxGeometry args={[0.85, 0.2, 0.1]} />
        <meshLambertMaterial color="#1565C0" flatShading />
      </mesh>
      <mesh position={[0, 1.3, 0.5]} castShadow>
        <boxGeometry args={[0.4, 0.15, 0.05]} />
        <meshLambertMaterial color="#B0BEC5" flatShading />
      </mesh>

      {/* Arms */}
      <group
        position={[0.55, 0.8, 0]}
        rotation={[isRunning ? Math.abs(armRotation) : armRotation, 0, 0]}
      >
        <mesh position={[0, -0.35, 0]} castShadow>
          <boxGeometry args={[0.2, 0.7, 0.2]} />
          <meshLambertMaterial color="#FFFFFF" flatShading />{' '}
          {/* White for Sasuke's sleeve */}
        </mesh>
      </group>
      <group
        position={[-0.55, 0.8, 0]}
        rotation={[isRunning ? Math.abs(armRotation) : -armRotation, 0, 0]}
      >
        <mesh position={[0, -0.35, 0]} castShadow>
          <boxGeometry args={[0.2, 0.7, 0.2]} />
          <meshLambertMaterial color="#FFFFFF" flatShading />{' '}
          {/* White for Sasuke's sleeve */}
        </mesh>
      </group>

      {/* Legs */}
      <group position={[0.25, 0, 0]} rotation={[legRotation, 0, 0]}>
        <mesh position={[0, -0.2, 0]} castShadow>
          <boxGeometry args={[0.25, 0.4, 0.25]} />
          <meshLambertMaterial color="#FFFFFF" flatShading />{' '}
          {/* White pants */}
        </mesh>
        <mesh position={[0, -0.45, 0.1]} castShadow>
          <boxGeometry args={[0.3, 0.2, 0.5]} />
          <meshLambertMaterial color="#0D47A1" flatShading />{' '}
          {/* Blue sandals */}
        </mesh>
      </group>
      <group position={[-0.25, 0, 0]} rotation={[-legRotation, 0, 0]}>
        <mesh position={[0, -0.2, 0]} castShadow>
          <boxGeometry args={[0.25, 0.4, 0.25]} />
          <meshLambertMaterial color="#FFFFFF" flatShading />{' '}
          {/* White pants */}
        </mesh>
        <mesh position={[0, -0.45, 0.1]} castShadow>
          <boxGeometry args={[0.3, 0.2, 0.5]} />
          <meshLambertMaterial color="#0D47A1" flatShading />{' '}
          {/* Blue sandals */}
        </mesh>
      </group>
    </group>
  );
};
